/**
 * AGC: Device Fingerprinting Utility
 * AGC: Generates and persists device ID for anti-bot tracking
 * AGC: Combines localStorage UUID with device characteristics hash
 */

const DEVICE_ID_KEY = 'origami_device_id'

/**
 * AGC: Generate device fingerprint hash
 * AGC: Combines screen resolution, timezone, language, and user agent subset
 * @returns {string} Device fingerprint hash
 */
function generateDeviceFingerprint(): string {
  if (typeof window === 'undefined') {
    // AGC: Server-side rendering - return random ID
    return `server_${Date.now()}_${Math.random().toString(36).substring(7)}`
  }

  const features = [
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.language,
    navigator.languages?.join(',') || '',
    navigator.platform,
    // AGC: User agent subset (first 50 chars to avoid too much variation)
    navigator.userAgent.substring(0, 50)
  ].join('|')

  // AGC: Simple hash function (for client-side)
  let hash = 0
  for (let i = 0; i < features.length; i++) {
    const char = features.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // AGC: Convert to 32-bit integer
  }

  return `fp_${Math.abs(hash).toString(16)}`
}

/**
 * AGC: Generate or retrieve device ID
 * AGC: Uses localStorage UUID + device fingerprint for uniqueness
 * @returns {string} Device ID
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') {
    return 'server_device_id'
  }

  try {
    // AGC: Try to get existing device ID from localStorage
    const existingId = localStorage.getItem(DEVICE_ID_KEY)
    if (existingId && existingId.length > 0) {
      return existingId
    }

    // AGC: Generate new device ID
    const uuid = generateUUID()
    const fingerprint = generateDeviceFingerprint()
    const deviceId = `${uuid}_${fingerprint}`

    // AGC: Store in localStorage
    localStorage.setItem(DEVICE_ID_KEY, deviceId)

    return deviceId
  } catch (error) {
    // AGC: Fallback if localStorage is not available
    console.warn('AGC: Failed to access localStorage, using session-only device ID:', error)
    return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`
  }
}

/**
 * AGC: Generate UUID v4
 * @returns {string} UUID string
 */
function generateUUID(): string {
  if (typeof window !== 'undefined' && 'crypto' in window && 'randomUUID' in crypto) {
    // AGC: Use native crypto.randomUUID if available (modern browsers)
    return crypto.randomUUID()
  }

  // AGC: Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * AGC: Clear device ID (for testing/debugging)
 * AGC: WARNING: This will reset the device ID - use only for testing!
 */
export function clearDeviceId(): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    localStorage.removeItem(DEVICE_ID_KEY)
    console.log('AGC: Device ID cleared')
  } catch (error) {
    console.warn('AGC: Failed to clear device ID:', error)
  }
}

/**
 * AGC: Get device ID hash (for privacy - hash before sending to backend)
 * AGC: Backend will use this to track device usage without storing raw device ID
 * @returns {Promise<string>} Hashed device ID
 */
export async function getDeviceIdHash(): Promise<string> {
  const deviceId = getDeviceId()
  
  if (typeof window !== 'undefined' && 'crypto' in window && 'subtle' in crypto) {
    try {
      // AGC: Use Web Crypto API for secure hashing
      const encoder = new TextEncoder()
      const data = encoder.encode(deviceId)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      return hashHex
    } catch (error) {
      console.warn('AGC: Failed to hash device ID with Web Crypto, using fallback:', error)
    }
  }

  // AGC: Fallback hash (simple, not cryptographically secure, but sufficient for device tracking)
  let hash = 0
  for (let i = 0; i < deviceId.length; i++) {
    const char = deviceId.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return `hash_${Math.abs(hash).toString(16)}`
}




